import { Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useAuth, useMaintenanceFlag } from '@halo/web-lib';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, type ReactNode } from 'react';
import { Layout } from './components/Layout';
import { VersionBadge } from './components/VersionBadge';
import { OnboardingGuard, OnboardingCompletedRedirect } from './components/OnboardingGuard';
import { LoadingScreen } from './components/LoadingScreen';
import { ImpersonationBanner } from './components/impersonation/ImpersonationBanner';
import * as LazyPages from './routes/lazy-pages';

function ErrorFallback({ resetError }: { readonly resetError: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-1000 dark:bg-grey-900">
      <div className="text-center max-w-md px-6">
        <h1 className="text-2xl font-bold text-charcoalGray-800 dark:text-charcoalGray-100 mb-3">
          Something went wrong
        </h1>
        <p className="text-charcoalGray-500 dark:text-charcoalGray-300 mb-6">
          An unexpected error occurred. Please try reloading the page.
        </p>
        <button
          type="button"
          onClick={resetError}
          className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

function RouteFallback() {
  return <LoadingScreen compact message="Loading page..." />;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function StoreRequired({ children }: { children: ReactNode }) {
  const { storeId, impersonation } = useAuth();

  // During impersonation the gateway rewrites X-Store-Id server-side,
  // so the client doesn't need a localStorage storeId to proceed.
  if (!storeId && !impersonation) {
    return <Navigate to="/select-store" replace />;
  }

  return <>{children}</>;
}

/**
 * Handles /store/:id — sets the store in auth context (same as SelectStorePage's
 * handleSelect) and redirects to the dashboard. Used by the agency stores page.
 */
function StoreRedirect() {
  const { id } = useParams<{ id: string }>();
  const { setStoreId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (id) {
      // Callers that navigate here (e.g. the Agency Stores page) pass
      // the store name via router state so the sidebar label updates
      // in lockstep with the storeId. Without this the sidebar reads a
      // stale halo_store_name from a previous session.
      const name = (location.state as { name?: string } | null)?.name;
      setStoreId(id, name);
      queryClient.clear();
      navigate('/', { replace: true });
    }
  }, [id, location.state, setStoreId, navigate, queryClient]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LazyPages.LoginPage />} />
          <Route path="/forgot-password" element={<LazyPages.ForgotPasswordPage />} />
          <Route path="/reset-password" element={<LazyPages.ResetPasswordPage />} />
          <Route path="/verify-email" element={<LazyPages.VerifyEmailPage />} />
          <Route path="/invitations/accept" element={<LazyPages.AcceptInvitationPage />} />
          <Route path="/connect/:token" element={<LazyPages.ConnectPage />} />
          <Route
            path="/accept-agency-invitation"
            element={
              <ProtectedRoute>
                <LazyPages.AcceptAgencyInvitationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agency/:agencyId"
            element={
              <ProtectedRoute>
                <LazyPages.AgencyLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<LazyPages.AgencyOverviewPage />} />
            <Route path="pods" element={<LazyPages.AgencyPodsPage />} />
            <Route path="stores" element={<LazyPages.AgencyStoresPage />} />
            <Route path="team" element={<LazyPages.AgencyTeamPage />} />
            <Route path="reports" element={<LazyPages.AgencyReportsPage />} />
            <Route path="notifications" element={<LazyPages.AgencyNotificationsPage />} />
            <Route path="api-keys" element={<LazyPages.AgencyApiKeysPage />} />
            <Route path="audit-log" element={<LazyPages.AgencyAuditLogPage />} />
            <Route path="settings" element={<LazyPages.AgencySettingsPage />} />
          </Route>
          <Route
            path="/pod/:podId"
            element={
              <ProtectedRoute>
                <LazyPages.PodLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<LazyPages.PodOverviewPage />} />
            <Route path="stores" element={<LazyPages.PodStoresPage />} />
            <Route path="team" element={<LazyPages.PodTeamPage />} />
            <Route path="api-keys" element={<LazyPages.PodApiKeysPage />} />
            <Route path="audit-log" element={<LazyPages.PodAuditLogPage />} />
            <Route path="settings" element={<LazyPages.PodSettingsPage />} />
          </Route>
          <Route path="/confirm-email-change" element={<LazyPages.ConfirmEmailChangePage />} />
          <Route
            path="/store/:id"
            element={
              <ProtectedRoute>
                <StoreRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/select-store"
            element={
              <ProtectedRoute>
                <LazyPages.SelectStorePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <LazyPages.AccountPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/import-legacy"
            element={
              <ProtectedRoute>
                <LazyPages.ImportLegacyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <StoreRequired>
                  <OnboardingCompletedRedirect>
                    <LazyPages.OnboardingPage />
                  </OnboardingCompletedRedirect>
                </StoreRequired>
              </ProtectedRoute>
            }
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <StoreRequired>
                  <OnboardingGuard>
                    <ImpersonationBanner />
                    <Layout>
                      <Routes>
                        <Route path="/" element={<LazyPages.OverviewPage />} />
                        <Route path="/integrations" element={<LazyPages.IntegrationsPage />} />
                        <Route path="/orders/*" element={<LazyPages.OrdersPage />} />
                        <Route path="/campaigns" element={<LazyPages.CampaignsHubPage />} />
                        <Route path="/halo-effect" element={<LazyPages.HaloEffectPage />} />
                        <Route path="/halo-effect-v2" element={<LazyPages.HaloEffectV2Page />} />
                        <Route path="/calendar" element={<LazyPages.CalendarPage />} />
                        <Route path="/reports" element={<LazyPages.ReportsPage />} />
                        <Route
                          path="/reports/:reportId"
                          element={<LazyPages.ReportBuilderPage />}
                        />
                        <Route path="/ask-halo/*" element={<LazyPages.AskHaloPage />} />
                        <Route path="/settings" element={<LazyPages.SettingsPage />} />
                        <Route path="/debug-data" element={<LazyPages.DebugDataPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Layout>
                  </OnboardingGuard>
                </StoreRequired>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <VersionBadge />
    </>
  );
}

/**
 * Maintenance gate. UI-only — backend does NOT enforce. When the flag is ON,
 * ALL pages render the maintenance screen. No bypass for admins, login, or
 * any other path. Flip the flag off (admin Cloud Run app) to restore access.
 */
function MaintenanceGate({ children }: { children: ReactNode }) {
  const maintenance = useMaintenanceFlag();

  if (maintenance.enabled) {
    return <LazyPages.MaintenancePage message={maintenance.message} />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <Suspense fallback={<RouteFallback />}>
        <MaintenanceGate>
          <AppRoutes />
        </MaintenanceGate>
      </Suspense>
    </Sentry.ErrorBoundary>
  );
}
